import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Lock, Eye, EyeOff, Loader2, Plus, Printer, Trash2, KeyRound, Store, Search, Pencil, Package } from "lucide-react";
import { toast } from "sonner";
import { API, api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ImportDialog from "@/components/ImportDialog";

const GST_RATES = [0, 5, 12, 18];
const UNITS = ["Strip", "Bottle", "Vial", "Tube", "Piece", "Box", "Sachet"];
const sapi = (t) => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${t}` } });
const EMPTY_ITEM = { name: "", batch: "", expiry: "", unit: "Strip", qty: 0, purchase_rate: 0, sale_rate: 0, gst_rate: 12, reorder_level: 10 };

function PwInput({ testId, value, onChange, placeholder }) {
    const [show, setShow] = useState(false);
    return (
        <div className="relative">
            <Input data-testid={testId} type={show ? "text" : "password"} value={value} onChange={onChange} placeholder={placeholder} className="pr-10" />
            <button type="button" data-testid={`${testId}-eye`} onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
        </div>
    );
}

export default function Pharmacy() {
    const [configured, setConfigured] = useState(null);
    const [token, setToken] = useState(localStorage.getItem("store_token") || "");
    const [view, setView] = useState("unlock");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [otp, setOtp] = useState("");
    const [newPw, setNewPw] = useState("");
    const [busy, setBusy] = useState(false);
    const [tab, setTab] = useState("inventory");
    const [items, setItems] = useState(null);
    const [bills, setBills] = useState(null);
    const [search, setSearch] = useState("");
    const [showItem, setShowItem] = useState(false);
    const [editing, setEditing] = useState(null);
    const [itemForm, setItemForm] = useState(EMPTY_ITEM);
    const [showBill, setShowBill] = useState(false);
    const [billType, setBillType] = useState("GST");
    const [customer, setCustomer] = useState({ name: "", phone: "" });
    const [lines, setLines] = useState([]);
    const [pickQ, setPickQ] = useState("");
    const [viewBill, setViewBill] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/store/status").then(({ data }) => setConfigured(data.configured)).catch(() => setConfigured(false));
    }, []);

    const load = async (t = token) => {
        try {
            const [{ data: it }, { data: b }] = await Promise.all([
                sapi(t).get("/store/items", { params: { search } }),
                sapi(t).get("/store/bills"),
            ]);
            setItems(it);
            setBills(b);
        } catch {
            localStorage.removeItem("store_token");
            setToken("");
        }
    };

    useEffect(() => { if (token) load(); }, [token, search]);

    const unlock = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/store/unlock", { password });
            localStorage.setItem("store_token", data.token);
            setToken(data.token);
            setPassword("");
            toast.success("Store unlocked");
        } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
    };

    const setupPw = async (e) => {
        e.preventDefault();
        if (password !== confirm) { toast.error("Passwords do not match"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/store/set", { password });
            localStorage.setItem("store_token", data.token);
            setToken(data.token);
            setConfigured(true);
            toast.success("Store password set");
        } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
    };

    const sendOtp = async () => {
        setBusy(true);
        try {
            await api.post("/store/forgot");
            toast.success("OTP sent to your registered email");
        } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
    };

    const resetPw = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/store/reset", { otp, new_password: newPw });
            localStorage.setItem("store_token", data.token);
            setToken(data.token);
            setView("unlock");
            toast.success("Store password reset");
        } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
    };

    const relock = () => { localStorage.removeItem("store_token"); setToken(""); };

    const saveItem = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...itemForm, qty: Number(itemForm.qty) || 0, purchase_rate: Number(itemForm.purchase_rate) || 0, sale_rate: Number(itemForm.sale_rate) || 0, gst_rate: Number(itemForm.gst_rate) || 0, reorder_level: Number(itemForm.reorder_level) || 0 };
            if (editing) await sapi(token).put(`/store/items/${editing.id}`, payload);
            else await sapi(token).post("/store/items", payload);
            toast.success(editing ? "Item updated" : "Item added");
            setShowItem(false);
            setEditing(null);
            setItemForm(EMPTY_ITEM);
            load();
        } catch (err) { toast.error(fmtErr(err)); } finally { setSaving(false); }
    };

    const removeItem = async (it) => {
        if (!window.confirm(`Delete ${it.name}?`)) return;
        try {
            await sapi(token).delete(`/store/items/${it.id}`);
            toast.success("Deleted");
            load();
        } catch (e) { toast.error(fmtErr(e)); }
    };

    const addLine = (item) => {
        if (lines.find((l) => l.item_id === item.id)) { toast.error("Already added"); return; }
        setLines([...lines, { item_id: item.id, name: item.name, qty: 1, rate: item.sale_rate, gst_rate: item.gst_rate, max: item.qty }]);
    };

    const setLine = (i, k, v) => setLines(lines.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));

    const totals = lines.reduce((acc, l) => {
        const taxable = (Number(l.qty) || 0) * (Number(l.rate) || 0);
        acc.sub += taxable;
        acc.gst += billType === "GST" ? taxable * (Number(l.gst_rate) || 0) / 100 : 0;
        return acc;
    }, { sub: 0, gst: 0 });

    const createBill = async (e) => {
        e.preventDefault();
        if (!lines.length) { toast.error("Add at least one item"); return; }
        setSaving(true);
        try {
            const payload = { customer_name: customer.name, customer_phone: customer.phone, bill_type: billType, items: lines.map((l) => ({ item_id: l.item_id, name: l.name, qty: Number(l.qty) || 1, rate: Number(l.rate) || 0, gst_rate: Number(l.gst_rate) || 0 })) };
            const { data } = await sapi(token).post("/store/bills", payload);
            toast.success(`Bill ${data.bill_no} created`);
            setShowBill(false);
            setLines([]);
            setCustomer({ name: "", phone: "" });
            load();
            openBill(data.id);
        } catch (err) { toast.error(fmtErr(err)); } finally { setSaving(false); }
    };

    const openBill = async (billId) => {
        try {
            const { data } = await sapi(token).get(`/store/bills/${billId}`);
            setViewBill(data);
        } catch (err) { toast.error(fmtErr(err)); }
    };

    const deleteBill = async (billId) => {
        if (!window.confirm("Delete this bill? Stock will be restored.")) return;
        try {
            await sapi(token).delete(`/store/bills/${billId}`);
            toast.success("Bill deleted, stock restored");
            load();
        } catch (err) { toast.error(fmtErr(err)); }
    };

    if (configured === null) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#E06D1F]" /></div>;

    if (!token) {
        return (
            <div className="min-h-[70vh] flex items-center justify-center animate-fade-up" data-testid="pharmacy-page">
                <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm" data-testid="store-lock-card">
                    <div className="flex items-center gap-3 mb-5">
                        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FFF4EC] text-[#E06D1F]"><Store className="h-5 w-5" /></span>
                        <div>
                            <h2 className="font-heading text-xl font-bold text-slate-900">Medical Store Access</h2>
                            <p className="text-xs text-slate-500">{configured ? "Enter the store operator password" : "Set a store operator password to continue"}</p>
                        </div>
                    </div>
                    {!configured ? (
                        <form onSubmit={setupPw} className="space-y-4">
                            <div className="space-y-1.5"><Label>Store Password (PIN or word+number)</Label><PwInput testId="store-setup-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 4 characters" /></div>
                            <div className="space-y-1.5"><Label>Confirm Password</Label><PwInput testId="store-setup-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat" /></div>
                            <Button data-testid="store-setup-btn" type="submit" disabled={busy} className="w-full bg-[#E06D1F] hover:bg-[#B45309] text-white">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Set Password & Enter</Button>
                        </form>
                    ) : view === "unlock" ? (
                        <form onSubmit={unlock} className="space-y-4">
                            <div className="space-y-1.5"><Label>Store Password</Label><PwInput testId="store-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Store password" /></div>
                            <Button data-testid="store-unlock-btn" type="submit" disabled={busy} className="w-full bg-[#E06D1F] hover:bg-[#B45309] text-white">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />} Unlock</Button>
                            <button type="button" data-testid="store-forgot-link" onClick={() => setView("forgot")} className="text-sm font-medium text-[#C2410C] hover:underline">Forgot password?</button>
                        </form>
                    ) : (
                        <form onSubmit={resetPw} className="space-y-4" data-testid="store-forgot-form">
                            <p className="text-sm text-slate-500">We'll send a 6-digit OTP to the registered email.</p>
                            <Button type="button" data-testid="store-send-otp-btn" variant="outline" onClick={sendOtp} disabled={busy} className="w-full">Send OTP to email</Button>
                            <div className="space-y-1.5"><Label>OTP</Label><Input data-testid="store-otp-input" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit OTP" maxLength={6} /></div>
                            <div className="space-y-1.5"><Label>New Store Password</Label><PwInput testId="store-new-password-input" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Min 4 characters" /></div>
                            <Button data-testid="store-reset-btn" type="submit" disabled={busy} className="w-full bg-[#E06D1F] hover:bg-[#B45309] text-white">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Reset & Enter</Button>
                            <button type="button" onClick={() => setView("unlock")} className="text-sm text-slate-500 hover:underline">Back to unlock</button>
                        </form>
                    )}
                </div>
            </div>
        );
    }

    const filteredPicks = (items || []).filter((i) => pickQ.trim() && i.name.toLowerCase().includes(pickQ.toLowerCase())).slice(0, 6);

    return (
        <div className="space-y-5 animate-fade-up" data-testid="pharmacy-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Medical Store</h1>
                    <p className="mt-1 text-base text-slate-500">Inventory & billing — GST / Non-GST bills</p>
                </div>
                <div className="flex gap-2">
                    <ImportDialog module="pharmacy" label="Import" storeAuth onDone={() => window.location.reload()} />
                    <Button data-testid="store-lock-btn" variant="outline" onClick={relock} className="gap-1.5"><Lock className="h-4 w-4" /> Lock</Button>
                    <Button data-testid="new-bill-btn" onClick={() => setShowBill(true)} className="bg-[#E06D1F] hover:bg-[#B45309] text-white gap-1.5"><Printer className="h-4 w-4" /> New Bill</Button>
                    <Button data-testid="add-item-btn" onClick={() => { setEditing(null); setItemForm(EMPTY_ITEM); setShowItem(true); }} variant="outline" className="gap-1.5"><Plus className="h-4 w-4" /> Add Item</Button>
                </div>
            </div>

            <div className="flex gap-1 border-b border-slate-200" data-testid="store-tabs">
                {[["inventory", "Inventory"], ["bills", "Bills"]].map(([k, l]) => (
                    <button key={k} data-testid={`store-tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors duration-150 ${tab === k ? "border-[#E06D1F] text-[#C2410C]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
                ))}
            </div>

            {tab === "inventory" && (
                <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="inventory-card">
                    <div className="px-5 py-3.5 border-b border-slate-100 relative max-w-sm">
                        <Search className="absolute left-8 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input data-testid="inventory-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search item..." className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-orange-500" />
                    </div>
                    {!items ? (
                        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#E06D1F]" /></div>
                    ) : items.length === 0 ? (
                        <p className="px-5 py-12 text-center text-sm text-slate-500" data-testid="inventory-empty">No items yet. Add your first stock item.</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead><tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
                                <th className="px-5 py-3">Item</th><th className="px-5 py-3 hidden sm:table-cell">Batch / Expiry</th><th className="px-5 py-3 text-right">Stock</th><th className="px-5 py-3 text-right hidden md:table-cell">MRP</th><th className="px-5 py-3 text-center hidden sm:table-cell">GST</th><th className="px-5 py-3"></th>
                            </tr></thead>
                            <tbody>
                                {items.map((i) => (
                                    <tr key={i.id} data-testid={`item-row-${i.id}`} className="border-t border-slate-100 hover:bg-[#FFF4EC]/60 transition-colors duration-100">
                                        <td className="px-5 py-3 font-medium text-slate-900"><span className="inline-flex items-center gap-2"><Package className="h-3.5 w-3.5 text-[#E06D1F]" />{i.name}</span><p className="text-xs text-slate-400">{i.unit}</p></td>
                                        <td className="px-5 py-3 hidden sm:table-cell text-slate-600 text-xs">{i.batch || "—"}{i.expiry ? ` · Exp ${i.expiry}` : ""}</td>
                                        <td className={`px-5 py-3 text-right font-semibold ${i.qty <= i.reorder_level ? "text-red-600" : "text-slate-900"}`} data-testid={`item-stock-${i.id}`}>{i.qty}{i.qty <= i.reorder_level && <span className="ml-1.5 rounded bg-red-50 border border-red-200 px-1 text-[10px] font-bold">LOW</span>}</td>
                                        <td className="px-5 py-3 text-right hidden md:table-cell">₹{i.sale_rate}</td>
                                        <td className="px-5 py-3 text-center hidden sm:table-cell text-xs text-slate-500">{i.gst_rate}%</td>
                                        <td className="px-5 py-3 text-right whitespace-nowrap">
                                            <Button data-testid={`item-edit-${i.id}`} size="sm" variant="ghost" onClick={() => { setEditing(i); setItemForm(i); setShowItem(true); }} className="text-slate-400 hover:text-[#E06D1F]"><Pencil className="h-4 w-4" /></Button>
                                            <Button data-testid={`item-delete-${i.id}`} size="sm" variant="ghost" onClick={() => removeItem(i)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {tab === "bills" && (
                <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="bills-card">
                    {!bills ? (
                        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#E06D1F]" /></div>
                    ) : bills.length === 0 ? (
                        <p className="px-5 py-12 text-center text-sm text-slate-500" data-testid="bills-empty">No bills yet.</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead><tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
                                <th className="px-5 py-3">Bill No</th><th className="px-5 py-3">Customer</th><th className="px-5 py-3 hidden sm:table-cell">Date</th><th className="px-5 py-3 text-center">Type</th><th className="px-5 py-3 text-right">Total</th><th className="px-5 py-3"></th>
                            </tr></thead>
                            <tbody>
                                {bills.map((b) => (
                                    <tr key={b.id} data-testid={`bill-row-${b.bill_no}`} className="border-t border-slate-100 hover:bg-[#FFF4EC]/60 transition-colors duration-100">
                                        <td className="px-5 py-3 font-mono2 text-xs font-semibold text-[#C2410C]">{b.bill_no}</td>
                                        <td className="px-5 py-3 font-medium text-slate-900">{b.customer_name}</td>
                                        <td className="px-5 py-3 hidden sm:table-cell text-slate-600">{b.date}</td>
                                        <td className="px-5 py-3 text-center"><span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${b.bill_type === "GST" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-100 text-slate-600 border-slate-200"}`} data-testid={`bill-type-${b.bill_no}`}>{b.bill_type === "GST" ? "GST" : "Non-GST"}</span></td>
                                        <td className="px-5 py-3 text-right font-semibold">₹{b.total.toLocaleString("en-IN")}</td>
                                        <td className="px-5 py-3 text-right whitespace-nowrap">
                                            <Button data-testid={`bill-view-${b.bill_no}`} size="sm" variant="outline" onClick={() => openBill(b.id)}>View</Button>
                                            <Button data-testid={`bill-delete-${b.bill_no}`} size="sm" variant="ghost" onClick={() => deleteBill(b.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            <Dialog open={showItem} onOpenChange={setShowItem}>
                <DialogContent className="max-w-lg" data-testid="item-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">{editing ? "Edit Item" : "Add Stock Item"}</DialogTitle>
                        <DialogDescription>Medicine/consumable stock with rates and GST.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={saveItem} className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5 col-span-2"><Label>Item Name *</Label><Input data-testid="item-name-input" required value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} placeholder="e.g. Dolo 650 Strip of 15" /></div>
                        <div className="space-y-1.5"><Label>Batch</Label><Input data-testid="item-batch-input" value={itemForm.batch} onChange={(e) => setItemForm({ ...itemForm, batch: e.target.value })} /></div>
                        <div className="space-y-1.5"><Label>Expiry</Label><Input data-testid="item-expiry-input" type="month" value={itemForm.expiry} onChange={(e) => setItemForm({ ...itemForm, expiry: e.target.value })} /></div>
                        <div className="space-y-1.5">
                            <Label>Unit</Label>
                            <select data-testid="item-unit-select" value={itemForm.unit} onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-orange-500 outline-none">
                                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1.5"><Label>Stock Qty</Label><Input data-testid="item-qty-input" type="number" min="0" value={itemForm.qty} onChange={(e) => setItemForm({ ...itemForm, qty: e.target.value })} /></div>
                        <div className="space-y-1.5"><Label>Purchase Rate ₹</Label><Input data-testid="item-purchase-input" type="number" min="0" step="0.01" value={itemForm.purchase_rate} onChange={(e) => setItemForm({ ...itemForm, purchase_rate: e.target.value })} /></div>
                        <div className="space-y-1.5"><Label>Sale Rate (MRP) ₹</Label><Input data-testid="item-sale-input" type="number" min="0" step="0.01" value={itemForm.sale_rate} onChange={(e) => setItemForm({ ...itemForm, sale_rate: e.target.value })} /></div>
                        <div className="space-y-1.5">
                            <Label>GST %</Label>
                            <select data-testid="item-gst-select" value={itemForm.gst_rate} onChange={(e) => setItemForm({ ...itemForm, gst_rate: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-orange-500 outline-none">
                                {GST_RATES.map((g) => <option key={g} value={g}>{g}%</option>)}
                            </select>
                        </div>
                        <div className="space-y-1.5"><Label>Low-stock Alert At</Label><Input data-testid="item-reorder-input" type="number" min="0" value={itemForm.reorder_level} onChange={(e) => setItemForm({ ...itemForm, reorder_level: e.target.value })} /></div>
                        <Button data-testid="item-save-btn" type="submit" disabled={saving} className="col-span-2 bg-[#E06D1F] hover:bg-[#B45309] text-white">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {editing ? "Save Changes" : "Add Item"}</Button>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={showBill} onOpenChange={setShowBill}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bill-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">New Store Bill</DialogTitle>
                        <DialogDescription>Pick items from inventory — stock is deducted on save.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={createBill} className="space-y-4">
                        <div className="flex gap-2" data-testid="bill-type-toggle">
                            {[["GST", "GST Bill (Tax Invoice)"], ["NON_GST", "Non-GST (Cash Memo)"]].map(([k, l]) => (
                                <button key={k} type="button" data-testid={`bill-type-${k.toLowerCase()}`} onClick={() => setBillType(k)} className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors duration-150 ${billType === k ? "bg-[#E06D1F] text-white border-[#E06D1F]" : "bg-white text-slate-600 border-slate-300 hover:border-orange-300"}`}>{l}</button>
                            ))}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5"><Label>Customer Name</Label><Input data-testid="bill-customer-input" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} placeholder="Walk-in" /></div>
                            <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="bill-phone-input" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} /></div>
                        </div>
                        <div className="space-y-1.5 relative">
                            <Label>Add Items</Label>
                            <Input data-testid="bill-item-search" value={pickQ} onChange={(e) => setPickQ(e.target.value)} placeholder="Search inventory..." />
                            {filteredPicks.length > 0 && (
                                <div className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg" data-testid="bill-item-results">
                                    {filteredPicks.map((i) => (
                                        <button key={i.id} type="button" data-testid={`bill-pick-${i.id}`} onClick={() => { addLine(i); setPickQ(""); }} className="w-full text-left px-4 py-2.5 hover:bg-[#FFF4EC] border-b border-slate-100 last:border-0">
                                            <span className="text-sm font-medium text-slate-900">{i.name}</span>
                                            <span className="ml-2 text-xs text-slate-500">₹{i.sale_rate} · stock {i.qty}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        {lines.length > 0 && (
                            <div className="space-y-2" data-testid="bill-lines">
                                {lines.map((l, i) => (
                                    <div key={l.item_id} className="grid grid-cols-[1fr_80px_100px_90px_32px] gap-2 items-center rounded-lg border border-slate-200 p-2.5">
                                        <p className="text-sm font-medium text-slate-900 truncate">{l.name}</p>
                                        <Input data-testid={`bill-qty-${i}`} type="number" min="1" max={l.max} value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} />
                                        <Input data-testid={`bill-rate-${i}`} type="number" min="0" step="0.01" value={l.rate} onChange={(e) => setLine(i, "rate", e.target.value)} />
                                        <p className="text-xs font-semibold text-right">₹{(((Number(l.qty) || 0) * (Number(l.rate) || 0)) * (1 + (billType === "GST" ? (Number(l.gst_rate) || 0) / 100 : 0))).toLocaleString("en-IN")}</p>
                                        <button type="button" data-testid={`bill-line-remove-${i}`} onClick={() => setLines(lines.filter((_, idx) => idx !== i))} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm space-y-1" data-testid="bill-totals">
                            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><b>₹{totals.sub.toLocaleString("en-IN")}</b></div>
                            {billType === "GST" && <div className="flex justify-between"><span className="text-slate-500">GST (CGST+SGST)</span><b>₹{totals.gst.toLocaleString("en-IN")}</b>}</div>}
                            <div className="flex justify-between text-base border-t border-slate-200 pt-1"><span className="font-semibold">Total</span><b className="text-[#C2410C]">₹{(totals.sub + totals.gst).toLocaleString("en-IN")}</b></div>
                        </div>
                        <Button data-testid="bill-submit-btn" type="submit" disabled={saving || !lines.length} className="w-full bg-[#E06D1F] hover:bg-[#B45309] text-white">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Issue Bill</Button>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={!!viewBill} onOpenChange={() => setViewBill(null)}>
                <DialogContent className="max-w-2xl" data-testid="bill-view-dialog">
                    <DialogHeader>
                        <DialogTitle className="sr-only">Store Bill</DialogTitle>
                        <DialogDescription className="sr-only">Printable store bill with totals and GST split.</DialogDescription>
                    </DialogHeader>
                    {viewBill && (
                        <div>
                            <div className="flex items-start justify-between border-b-2 border-[#E06D1F] pb-4">
                                <div>
                                    <img src="/zen-logo.webp" alt="Zen Hospital" className="h-12 w-auto" />
                                    <p className="mt-1.5 text-xs text-slate-500 max-w-[280px]">{viewBill.doctor.clinic_address}</p>
                                    <p className="text-xs text-slate-500">Ph: {viewBill.doctor.clinic_phone}</p>
                                </div>
                                <div className="text-right">
                                    <h3 className="font-heading text-lg font-bold text-slate-900">{viewBill.bill.bill_type === "GST" ? "TAX INVOICE" : "CASH MEMO"}</h3>
                                    <p className="text-sm font-mono2 font-semibold text-[#C2410C]" data-testid="bill-view-no">{viewBill.bill.bill_no}</p>
                                    <p className="text-xs text-slate-500">Date: {viewBill.bill.date}</p>
                                </div>
                            </div>
                            <p className="py-3 text-sm"><span className="text-slate-500">Customer:</span> <b>{viewBill.bill.customer_name}</b>{viewBill.bill.customer_phone ? ` · ${viewBill.bill.customer_phone}` : ""}</p>
                            <table className="w-full text-sm border border-slate-200">
                                <thead><tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 text-left">
                                    <th className="px-3 py-2">Item</th><th className="px-3 py-2 text-center">Qty</th><th className="px-3 py-2 text-right">Rate</th>{viewBill.bill.bill_type === "GST" && <th className="px-3 py-2 text-right">GST</th>}<th className="px-3 py-2 text-right">Amount</th>
                                </tr></thead>
                                <tbody>
                                    {viewBill.bill.items.map((it, i) => (
                                        <tr key={i} className="border-t border-slate-100">
                                            <td className="px-3 py-2">{it.name}</td>
                                            <td className="px-3 py-2 text-center">{it.qty}</td>
                                            <td className="px-3 py-2 text-right">₹{it.rate}</td>
                                            {viewBill.bill.bill_type === "GST" && <td className="px-3 py-2 text-right">{it.gst_rate}%</td>}
                                            <td className="px-3 py-2 text-right font-medium">₹{it.amount.toLocaleString("en-IN")}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="flex justify-end mt-3">
                                <div className="w-64 text-sm space-y-1">
                                    <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>₹{viewBill.bill.subtotal.toLocaleString("en-IN")}</span></div>
                                    {viewBill.bill.bill_type === "GST" && (
                                        <>
                                            <div className="flex justify-between"><span className="text-slate-500">CGST</span><span>₹{(viewBill.bill.gst_total / 2).toLocaleString("en-IN")}</span></div>
                                            <div className="flex justify-between"><span className="text-slate-500">SGST</span><span>₹{(viewBill.bill.gst_total / 2).toLocaleString("en-IN")}</span></div>
                                        </>
                                    )}
                                    <div className="flex justify-between font-bold text-base border-t border-slate-300 pt-1"><span>Grand Total</span><span className="text-[#C2410C]">₹{viewBill.bill.total.toLocaleString("en-IN")}</span></div>
                                </div>
                            </div>
                            <div className="mt-5 flex justify-end no-print">
                                <Button data-testid="bill-print-btn" onClick={() => window.print()} className="bg-[#E06D1F] hover:bg-[#B45309] text-white gap-1.5"><Printer className="h-4 w-4" /> Print Bill</Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
